import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { IAuditLogItemResponse } from 'models/auditLog';
import { IRole } from 'models/role';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import { EPermissionRelatedModule, permissionsGroups } from 'models/permissions';
import { IParsedPermissions, useRoles } from 'pages/RolesPage/hooks';
import { EAuditLogEvents } from 'pages/AuditLogPage/helpers';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';

interface IAdministrationRoleEventDetails {
  auditLog: IAuditLogItemResponse;
}

const AdministrationRoleEventDetails = ({ auditLog }: IAdministrationRoleEventDetails) => {
  const [role, setRole] = useState<IRole | null>();
  const [previousRole, setPreviousRole] = useState<IRole | null>();
  const [previousPermissionModules, setPreviousPermissionModules] = useState<
    EPermissionRelatedModule[]
  >([]);
  const [permissionModules, setPermissionModules] = useState<EPermissionRelatedModule[]>([]);
  const [parsedPermissions, setParsedPermissions] = useState<IParsedPermissions>({});
  const { getPermissions, parsePermissions, permissions } = useRoles();
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    if (auditLog?.previousObjectValue) {
      const previousObjectValue = auditLog.previousObjectValue as any;
      const roleData: IRole = {
        createdDate: '',
        description: previousObjectValue.description || '',
        formPermissionIds: previousObjectValue.formPermissionIds || {},
        name: previousObjectValue.name || '',
        permissionIds: previousObjectValue.permissionIds || [],
        uniqueId: '',
      };
      setPreviousRole(roleData);
    }
    if (auditLog?.postObjectValue) {
      const postObjectValue = auditLog.postObjectValue as any;
      setRole(postObjectValue);
    }
  }, [auditLog]);

  useEffect(() => {
    getPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setParsedPermissions(parsePermissions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);

  useEffect(() => {
    if (permissions && parsedPermissions && previousRole) {
      const modulesWithMatchingPermissions = permissionsGroups
        .map((group) => group.module)
        .filter((module) => {
          const permissionsForModule = parsedPermissions[module];
          if (!permissionsForModule) return false;

          return permissionsForModule
            .map((perm) => perm.uniqueId)
            .some((id) => previousRole.permissionIds?.includes(id));
        });

      setPreviousPermissionModules(modulesWithMatchingPermissions);
    }
  }, [permissions, parsedPermissions, previousRole]);

  useEffect(() => {
    if (permissions && parsedPermissions && role) {
      const modulesWithMatchingPermissions = permissionsGroups
        .map((group) => group.module)
        .filter((module) => {
          const permissionsForModule = parsedPermissions[module];
          if (!permissionsForModule) return false;

          return permissionsForModule
            .map((perm) => perm.uniqueId)
            .some((id) => role.permissionIds?.includes(id));
        });

      setPermissionModules(modulesWithMatchingPermissions);
    }
  }, [permissions, parsedPermissions, role]);

  return (
    <>
      {previousRole && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_ROLE
                ? `${t('roleDetails.header')} ${t('auditLogDetails.before.event.title')}`
                : `${t('roleDetails.header')} ${t('auditLogDetails.after.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            pageType={EDetailsPageTypes.auditLogView}
            title={previousRole.name}
            disableActions
          >

            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('roleDetails.description.label')}
              </Typography>
              <Typography variant="body2">
                {previousRole?.description ? previousRole.description : '---'}
              </Typography>
            </Stack>

            {!!previousRole?.permissionIds?.length && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: colors.primary500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {t('roleDetails.selectedPermissions.label')}
                  </Typography>
                </Grid>

                {permissionsGroups
                  .filter((it) => previousPermissionModules.includes(it.module))
                  .map(
                    (group) =>
                      parsedPermissions?.[group.module] && (
                        <Grid size={{ xs: 12, md: 6, lg: 3 }} key={group.module} sx={{ mb: 2 }}>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{t(group.title)}</Typography>

                            <div>
                              {parsedPermissions?.[group.module].map(
                                (permission) =>
                                  previousRole?.permissionIds?.includes(
                                    permission.uniqueId,
                                  ) && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {permission.name}
                                    </Typography>
                                  ),
                              )}
                            </div>
                          </Box>
                        </Grid>
                      ),
                  )}
              </>
            )}
          </DetailsTemplate>
        </>
      )}

      {previousRole && auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_ROLE && (
        <AuditLogEventName sx={{ marginTop: '20px' }}>
          <Typography variant="h5">
            {`${t('roleDetails.header')} ${t('auditLogDetails.post.event.title')}`}
          </Typography>
        </AuditLogEventName>
      )}

      {role && (
        <DetailsTemplate
          pageType={EDetailsPageTypes.auditLogView}
          title={role.name}
          redirectToView={() =>
            navigate(`${ERouteLinks.ViewRole.replace(':id', role.uniqueId)}`)
          }
          disableActions={!role.uniqueId}
        >

          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('roleDetails.description.label')}
            </Typography>
            <Typography variant="body2">
              {role?.description ? role.description : '---'}
            </Typography>
          </Stack>

          {!!role?.permissionIds?.length && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colors.primary500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {t('roleDetails.selectedPermissions.label')}
                </Typography>
              </Grid>

              {permissionsGroups
                .filter((it) => permissionModules.includes(it.module))
                .map(
                  (group) =>
                    parsedPermissions?.[group.module] && (
                      <Grid size={{ xs: 12, md: 6, lg: 3 }} key={group.module} sx={{ mb: 2 }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">{t(group.title)}</Typography>

                          <div>
                            {parsedPermissions?.[group.module].map(
                              (permission) =>
                                role?.permissionIds?.includes(permission.uniqueId) && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {permission.name}
                                  </Typography>
                                ),
                            )}
                          </div>
                        </Box>
                      </Grid>
                    ),
                )}
            </>
          )}
        </DetailsTemplate>
      )}
    </>
  );
};

export default AdministrationRoleEventDetails;
